// Resize an uploaded image down to a max width while preserving aspect ratio,
// then return a JPEG data URL. Used to keep menu/receipt scans under
// Cloudflare/iOS request body limits.

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Reject HEIC / HEIF / unsupported formats up-front so the user gets a clear
 * error instead of silently losing the upload. iOS WKWebView usually
 * auto-converts HEIC → JPEG when uploading via <input type=file>, but the
 * Capacitor WebView can occasionally pass HEIC through unchanged, especially
 * on iOS 17+. Image() can't decode HEIC, which then breaks the resize and
 * leaves the user staring at a card with no photo.
 */
function isUnsupportedFormat(file) {
  const name = (file?.name || "").toLowerCase();
  const type = (file?.type || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif")) return true;
  if (/\.(heic|heif)$/.test(name)) return true;
  return false;
}

export async function resizeToDataUrl(file, maxW, quality) {
  if (isUnsupportedFormat(file)) {
    const err = new Error(
      "HEIC / HEIF photos aren't supported. In iOS Settings → Camera → Formats, switch to 'Most Compatible'."
    );
    err.code = "UNSUPPORTED_FORMAT";
    throw err;
  }
  const dataUrl = await readAsDataUrl(file);
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = img.width > maxW ? maxW / img.width : 1;
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL("image/jpeg", quality);
        if (!out || !out.startsWith("data:image/jpeg")) {
          throw new Error("Encoder returned an unexpected format");
        }
        resolve(out);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => {
      const err = new Error(
        "We couldn't read this image. Try a different one (PNG or JPEG)."
      );
      err.code = "DECODE_FAILED";
      reject(err);
    };
    img.src = dataUrl;
  });
}

